import React from 'react'
import useStore from '@/store'
import classNames from 'classnames'
import type { Props } from './types'
import type {
  Location as TLocation,
  Resident as TResident,
} from '@/store/types'
import LocationModalContent from '../location-modal-content'
import ResidentsModalContent from '../residents-modal-content'
import Location from '@/components/locations/location'
import styles from './style.module.scss'
import Flag from '@/components/flag'
import WrappedText from '@/components/wrapped-text'

const TenantsModalContent: React.FC<Props> = ({
  tenants,
  residents,
  type,
  side,
  overviewLabel,
  overviewImageURL
}) => {
  const modals = useStore((state) => state.modals)
  const setModal = useStore((state) => state.setModal)
  const goLastModal = useStore((state) => state.goLastModal)
  const activeCategoryFilter = useStore((state) => state.activeCategoryFilter)
  const sounds = useStore((state) => state.sounds)

  const classes = classNames(styles['tenants-modal-content'], [
    styles[`type--${type}`],
  ])

  function onTenantClick(r: TResident) {
    sounds?.click1?.play()
    if (r.attributes.Main) {
      setModal(
        {
          content: (
            <ResidentsModalContent
              building={r.attributes.Building.data}
              residents={residents?.filter(
                (other) =>
                  other.attributes.Building.data?.attributes.Slug
                    === r.attributes.Building.data?.attributes.Slug
              )}
              type={type}
              side={side}
            />
          ),
          type: 'resident',
          closeAction: () => setModal(modals[side]!, side),
        },
        side
      )
    } else {
      setModal(
        {
          content: (
            <LocationModalContent
              title={r.attributes.Title}
              text={r.attributes.Data.Text}
              image={r.attributes.Data.Image?.Image}
              caption={r.attributes.Data.Image?.Caption}
            />
          ),
          type: 'resident',
          closeAction: () => goLastModal(side),
        },
        side
      )
    }
  }

  return (
    <div className={classes}>
      <div className={styles.axonometries}>
        {overviewLabel ? (
            <WrappedText className={styles.title} text={overviewLabel} />
          ) : null
        }
        <figure className={styles.axonometry}>
          <>
            <img src={overviewImageURL} alt="" />
            {(() => {
              return tenants?.map((t) => (
                <>
                  {(() => {
                    if (!t.attributes.Main || !t.attributes.OverviewClickTarget)
                      return null

                    const rect = t.attributes.OverviewClickTarget
                    return (
                      <div
                        className={styles['click-ghost-main']}
                        onClick={() => onTenantClick(t)}
                        style={{
                          left: `${Math.min(rect.X1, rect.X2)}%`,
                          top: `${(100 - Math.max(rect.Y1, rect.Y2))}%`,
                          width: `${Math.abs(rect.X2 - rect.X1)}%`,
                          height: `${Math.abs(rect.Y2 - rect.Y1)}%`
                        }}
                      />
                    )
                  })()}
                  <Flag
                    key={t?.id}
                    text={t?.attributes.Label}
                    placement={t?.attributes.Flag}
                    main={t?.attributes.Main}
                    type={type}
                    category={t.attributes.Category.data}
                    highlighted={
                      activeCategoryFilter[side] ===
                      t.attributes.Category.data.attributes.Slug
                    }
                    clickHandler={() => onTenantClick(t)}
                  >
                    {t?.attributes.Main && (
                      <div className={styles.residents}>
                        {(() => {
                          return residents
                            ?.filter((r) =>
                              Object.values(activeCategoryFilter).includes(
                                r.attributes.Category.data.attributes.Slug
                              ) && t.attributes.Building.data?.attributes.Slug
                                === r.attributes.Building.data?.attributes.Slug
                            )
                            .reduce((acc, cur) => {
                              return acc.filter(
                                (item) =>
                                  item.attributes.Category.data.attributes
                                    .Slug ===
                                  cur.attributes.Category.data.attributes.Slug
                              ).length < 4 &&
                                !acc.find((item) => item.id === cur.id)
                                ? acc.concat(cur)
                                : acc
                            }, [] as (TLocation | TResident)[])
                            .map((r) => (
                              <Location
                                key={r.id}
                                data={r}
                                isActive={false}
                                isFiltered={false}
                                isResident={true}
                              />
                            ))
                        })()}
                      </div>
                    )}
                  </Flag>
                </>
              ))
            })()}
          </>
        </figure>
      </div>
    </div>
  )
}

export default React.memo(TenantsModalContent)
